/**
 * Billing Module Types
 * Matches backend DTOs and enums for credits-based billing system
 */

// ==================== Enums ====================

export enum BillingType {
  PREPAID = 'PREPAID',
  POSTPAID = 'POSTPAID',
}

export enum ServiceType {
  // Voice Services
  VOICE_INBOUND = 'VOICE_INBOUND',
  VOICE_OUTBOUND = 'VOICE_OUTBOUND',
  VOICE_IVR = 'VOICE_IVR',
  VOICE_RECORDING = 'VOICE_RECORDING',

  // SMS Services
  SMS_LOCAL = 'SMS_LOCAL',
  SMS_INTERNATIONAL = 'SMS_INTERNATIONAL',
  SMS_PREMIUM = 'SMS_PREMIUM',

  // WhatsApp Services
  WHATSAPP_SESSION = 'WHATSAPP_SESSION',
  WHATSAPP_TEMPLATE = 'WHATSAPP_TEMPLATE',
  WHATSAPP_MEDIA = 'WHATSAPP_MEDIA',
  // Meta's pricing categories; each falls back to WHATSAPP_TEMPLATE when it has no rate.
  WHATSAPP_MARKETING = 'WHATSAPP_MARKETING',
  WHATSAPP_UTILITY = 'WHATSAPP_UTILITY',
  WHATSAPP_AUTHENTICATION = 'WHATSAPP_AUTHENTICATION',

  // RCS Services
  RCS_MESSAGE = 'RCS_MESSAGE',
  RCS_RICH_CARD = 'RCS_RICH_CARD',
  RCS_CAROUSEL = 'RCS_CAROUSEL',

  // OBD Services
  OBD_CALL = 'OBD_CALL',
  OBD_TTS = 'OBD_TTS',

  // Other Services
  DID_RENTAL = 'DID_RENTAL',
  PHONE_NUMBER_RENTAL = 'PHONE_NUMBER_RENTAL',
  API_CALL = 'API_CALL',
}

/**
 * Which length of a voice call a rate bills. DURATION (the default) is the whole call,
 * ringing included — GG's invoice basis. TALK_TIME starts at the answer.
 */
export enum BillingBasis {
  DURATION = 'DURATION',
  TALK_TIME = 'TALK_TIME',
}

export enum TransactionType {
  CREDIT_PURCHASE = 'CREDIT_PURCHASE',
  CREDIT_ADJUSTMENT = 'CREDIT_ADJUSTMENT',
  CREDIT_REFUND = 'CREDIT_REFUND',
  CREDIT_EXPIRY = 'CREDIT_EXPIRY',
  DEBIT_USAGE = 'DEBIT_USAGE',
  DEBIT_PENALTY = 'DEBIT_PENALTY',
}

export enum InvoiceStatus {
  DRAFT = 'DRAFT',
  PENDING = 'PENDING',
  PAID = 'PAID',
  PARTIALLY_PAID = 'PARTIALLY_PAID',
  OVERDUE = 'OVERDUE',
  CANCELLED = 'CANCELLED',
  REFUNDED = 'REFUNDED',
}

export enum PaymentMethod {
  BANK_TRANSFER = 'BANK_TRANSFER',
  CREDIT_CARD = 'CREDIT_CARD',
  DEBIT_CARD = 'DEBIT_CARD',
  MOBILE_MONEY = 'MOBILE_MONEY',
  PAYPAL = 'PAYPAL',
  STRIPE = 'STRIPE',
  CASH = 'CASH',
  CHEQUE = 'CHEQUE',
  WALLET = 'WALLET',
}

// ==================== DTOs ====================

export interface CreditAccountDTO {
  id: number;
  organizationId: string;
  organizationName?: string;
  billingType: BillingType;
  creditBalance: number;
  creditLimit?: number;
  lowBalanceThreshold?: number;
  autoRechargeEnabled?: boolean;
  autoRechargeAmount?: number;
  autoRechargeThreshold?: number;
  currency: string;
  active: boolean;
  billingEmail?: string;
  billingAddress?: string;
  taxId?: string;
  /** Credit control: null = follow the platform default (cpass.billing.prepaid.enforce). */
  enforceMode?: EnforceMode | null;
  graceAmount?: number | null;
  autoSuspend?: boolean | null;
  /** null = the platform's default tiers. */
  alertTierPercents?: number[] | null;
  createdAt: string;
  updatedAt?: string;
}

export interface ServiceRateDTO {
  id: number;
  organizationId?: string;
  organizationName?: string;
  serviceType: ServiceType;
  ratePerUnit: number;
  unitDescription?: string;
  minimumCharge?: number;
  billingIncrement?: number;
  /** Calls shorter than this many seconds get shortCallDiscountPercent off. */
  shortCallSeconds?: number;
  shortCallDiscountPercent?: number;
  billingBasis?: BillingBasis;
  currency: string;
  active: boolean;
  effectiveFrom: string;
  effectiveTo?: string;
  description?: string;
  createdAt: string;
  updatedAt?: string;
}

export interface CreditTransactionDTO {
  id: number;
  organizationId: string;
  organizationName?: string;
  transactionType: TransactionType;
  serviceType?: ServiceType;
  amount: number;
  balanceBefore: number;
  balanceAfter: number;
  currency: string;
  referenceId?: string;
  referenceType?: string;
  description?: string;
  performedBy?: string;
  /** Voice: minutes billed, pulse-rounded, before any discount. */
  billedMinutes?: number;
  /** Set when this organisation paid for another's usage (a provider for its business). */
  onBehalfOfOrganizationId?: string;
  onBehalfOfOrganizationName?: string;
  /** Voice: the carrier behind the call's number (Tata landline, Twilio, Vobiz…). */
  carrier?: string;
  /** Set on a top-up that opened a credit grant (POST /credits/add). */
  grantId?: number | null;
  /** Server UTC; null = the grant never expires. */
  grantExpiresAt?: string | null;
  createdAt: string;
}

export interface InvoiceItemDTO {
  id: number;
  serviceType?: ServiceType;
  description: string;
  quantity: number;
  unitPrice: number;
  totalPrice: number;
  creditsEquivalent?: number;
}

export interface PaymentDTO {
  id: number;
  paymentReference: string;
  organizationId: string;
  organizationName?: string;
  invoiceId?: number;
  invoiceNumber?: string;
  amount: number;
  currency: string;
  paymentMethod: PaymentMethod;
  paymentDate: string;
  transactionId?: string;
  successful: boolean;
  failureReason?: string;
  notes?: string;
  recordedBy?: string;
  createdAt: string;
}

export interface InvoiceDTO {
  id: number;
  invoiceNumber: string;
  organizationId: string;
  organizationName?: string;
  status: InvoiceStatus;
  invoiceDate: string;
  dueDate: string;
  billingPeriodStart?: string;
  billingPeriodEnd?: string;
  subtotal: number;
  taxRate: number;
  taxAmount: number;
  discountAmount: number;
  totalAmount: number;
  amountPaid: number;
  balanceDue: number;
  creditsPurchased?: number;
  /** USAGE (from the ledger; paying it settles a postpaid balance) or CREDIT_PURCHASE. */
  kind?: 'USAGE' | 'CREDIT_PURCHASE';
  currency: string;
  billingName?: string;
  billingEmail?: string;
  billingAddress?: string;
  notes?: string;
  items?: InvoiceItemDTO[];
  payments?: PaymentDTO[];
  createdAt: string;
  updatedAt?: string;
}

export interface UsageSummaryDTO {
  id: number;
  organizationId: string;
  organizationName?: string;
  serviceType: ServiceType;
  usageDate: string;
  totalUnits: number;
  totalCredits: number;
  totalCount: number;
  successfulCount: number;
  failedCount: number;
  currency: string;
  createdAt: string;
}

export interface TopConsumerDTO {
  organizationId: string;
  organizationName?: string;
  creditsUsed: number;
  currentBalance?: number;
}

export interface BillingStatsDTO {
  totalCreditsBalance?: number;
  totalCreditsUsedToday?: number;
  totalCreditsUsedThisMonth?: number;
  totalRevenue?: number;
  totalPendingPayments?: number;
  totalAccounts: number;
  prepaidAccounts: number;
  postpaidAccounts: number;
  lowBalanceAccounts: number;
  pendingInvoices: number;
  overdueInvoices: number;
  totalOutstanding?: number;
  usageByServiceType?: Record<string, number>;
  topConsumers?: TopConsumerDTO[];
}

// ==================== Request Types ====================

export interface CreateCreditAccountRequest {
  organizationId: string;
  billingType: BillingType;
  initialCredits?: number;
  creditLimit?: number;
  lowBalanceThreshold?: number;
  autoRechargeEnabled?: boolean;
  autoRechargeAmount?: number;
  autoRechargeThreshold?: number;
  currency?: string;
  billingEmail?: string;
  billingAddress?: string;
  taxId?: string;
}

export interface CreateServiceRateRequest {
  organizationId?: string;
  serviceType: ServiceType;
  ratePerUnit: number;
  unitDescription?: string;
  minimumCharge?: number;
  billingIncrement?: number;
  shortCallSeconds?: number;
  shortCallDiscountPercent?: number;
  billingBasis?: BillingBasis;
  currency?: string;
  effectiveFrom: string;
  effectiveTo?: string;
  description?: string;
}

export interface AddCreditsRequest {
  organizationId: string;
  amount: number;
  description?: string;
  referenceId?: string;
  /** Absent = the server's PURCHASE. */
  source?: CreditSource;
  /** Server UTC LocalDateTime (`2026-12-31T18:30:00`); absent = never expires. */
  expiresAt?: string;
  /** Lower is drawn first; absent = the server's 100. */
  priority?: number;
}

export interface DeductCreditsRequest {
  organizationId: string;
  serviceType: ServiceType;
  amount: number;
  units?: number;
  referenceId?: string;
  referenceType?: string;
  description?: string;
}

export interface InvoiceItemRequest {
  serviceType?: string;
  description: string;
  quantity: number;
  unitPrice: number;
  creditsEquivalent?: number;
}

export interface CreateInvoiceRequest {
  organizationId: string;
  invoiceDate: string;
  dueDate: string;
  billingPeriodStart?: string;
  billingPeriodEnd?: string;
  taxRate?: number;
  discountAmount?: number;
  creditsPurchased?: number;
  billingName?: string;
  billingEmail?: string;
  billingAddress?: string;
  notes?: string;
  items?: InvoiceItemRequest[];
}

export interface RecordPaymentRequest {
  organizationId: string;
  invoiceId?: number;
  amount: number;
  paymentMethod: PaymentMethod;
  paymentDate?: string;
  transactionId?: string;
  notes?: string;
  /** ISO 4217. Absent, the server takes the invoice's, else the account's. */
  currency?: string;
  addCredits?: boolean;
  creditsToAdd?: number;
}

// ==================== Filter Types ====================

export interface CreditAccountFilters {
  billingType?: BillingType;
  active?: boolean;
}

export interface ServiceRateFilters {
  organizationId?: string;
  serviceType?: ServiceType;
  active?: boolean;
}

export interface TransactionFilters {
  organizationId?: string;
  transactionType?: TransactionType;
  serviceType?: ServiceType;
  startDate?: string;
  endDate?: string;
}

export interface InvoiceFilters {
  organizationId?: string;
  status?: InvoiceStatus;
  startDate?: string;
  endDate?: string;
}

export interface PaymentFilters {
  organizationId?: string;
  paymentMethod?: PaymentMethod;
  startDate?: string;
  endDate?: string;
}

export interface UsageFilters {
  organizationId?: string;
  serviceType?: ServiceType;
  startDate?: string;
  endDate?: string;
}

// ==================== Display Helpers ====================

export const serviceTypeLabels: Record<ServiceType, string> = {
  [ServiceType.VOICE_INBOUND]: 'Voice Inbound',
  [ServiceType.VOICE_OUTBOUND]: 'Voice Outbound',
  [ServiceType.VOICE_IVR]: 'Voice IVR',
  [ServiceType.VOICE_RECORDING]: 'Voice Recording',
  [ServiceType.SMS_LOCAL]: 'SMS Local',
  [ServiceType.SMS_INTERNATIONAL]: 'SMS International',
  [ServiceType.SMS_PREMIUM]: 'SMS Premium',
  [ServiceType.WHATSAPP_SESSION]: 'WhatsApp Session',
  [ServiceType.WHATSAPP_TEMPLATE]: 'WhatsApp Template',
  [ServiceType.WHATSAPP_MEDIA]: 'WhatsApp Media',
  [ServiceType.WHATSAPP_MARKETING]: 'WhatsApp Marketing',
  [ServiceType.WHATSAPP_UTILITY]: 'WhatsApp Utility',
  [ServiceType.WHATSAPP_AUTHENTICATION]: 'WhatsApp Authentication',
  [ServiceType.RCS_MESSAGE]: 'RCS Message',
  [ServiceType.RCS_RICH_CARD]: 'RCS Rich Card',
  [ServiceType.RCS_CAROUSEL]: 'RCS Carousel',
  [ServiceType.OBD_CALL]: 'OBD Call',
  [ServiceType.OBD_TTS]: 'OBD TTS',
  [ServiceType.DID_RENTAL]: 'DID Rental',
  [ServiceType.PHONE_NUMBER_RENTAL]: 'Phone Number Rental',
  [ServiceType.API_CALL]: 'API Call',
};

export const transactionTypeLabels: Record<TransactionType, string> = {
  [TransactionType.CREDIT_PURCHASE]: 'Credit Purchase',
  [TransactionType.CREDIT_ADJUSTMENT]: 'Credit Adjustment',
  [TransactionType.CREDIT_REFUND]: 'Credit Refund',
  [TransactionType.CREDIT_EXPIRY]: 'Credit Expiry',
  [TransactionType.DEBIT_USAGE]: 'Usage Deduction',
  [TransactionType.DEBIT_PENALTY]: 'Penalty Deduction',
};

export const invoiceStatusLabels: Record<InvoiceStatus, string> = {
  [InvoiceStatus.DRAFT]: 'Draft',
  [InvoiceStatus.PENDING]: 'Pending',
  [InvoiceStatus.PAID]: 'Paid',
  [InvoiceStatus.PARTIALLY_PAID]: 'Partially Paid',
  [InvoiceStatus.OVERDUE]: 'Overdue',
  [InvoiceStatus.CANCELLED]: 'Cancelled',
  [InvoiceStatus.REFUNDED]: 'Refunded',
};

export const paymentMethodLabels: Record<PaymentMethod, string> = {
  [PaymentMethod.BANK_TRANSFER]: 'Bank Transfer',
  [PaymentMethod.CREDIT_CARD]: 'Credit Card',
  [PaymentMethod.DEBIT_CARD]: 'Debit Card',
  [PaymentMethod.MOBILE_MONEY]: 'Mobile Money',
  [PaymentMethod.PAYPAL]: 'PayPal',
  [PaymentMethod.STRIPE]: 'Stripe',
  [PaymentMethod.CASH]: 'Cash',
  [PaymentMethod.CHEQUE]: 'Cheque',
  [PaymentMethod.WALLET]: 'Wallet',
};

export const billingBasisLabels: Record<BillingBasis, string> = {
  [BillingBasis.DURATION]: 'Duration (whole call, ringing included)',
  [BillingBasis.TALK_TIME]: 'Talk time (from answer)',
};

/** Currencies a rate or account may be in. INR is what every live account uses. */
export const BILLING_CURRENCIES = ['INR', 'USD'] as const;

/**
 * An amount in its OWN currency. Billing amounts were all formatted as USD, so an INR
 * account's ₹10,00,000 limit read "$1,000,000.00".
 */
export const formatMoney = (value: number | undefined | null, currency = 'INR', fractionDigits = 2): string => {
  if (value === undefined || value === null) return '-';
  try {
    return new Intl.NumberFormat(currency === 'INR' ? 'en-IN' : 'en-US', {
      style: 'currency',
      currency,
      minimumFractionDigits: fractionDigits,
      maximumFractionDigits: Math.max(fractionDigits, 4),
    }).format(value);
  } catch {
    return `${value} ${currency}`;
  }
};

export const billingTypeLabels: Record<BillingType, string> = {
  [BillingType.PREPAID]: 'Prepaid',
  [BillingType.POSTPAID]: 'Postpaid',
};

// ==================== Billing profiles (phase 1) ====================

export type GstRegistration = 'REGULAR' | 'COMPOSITION' | 'SEZ_UNIT' | 'SEZ_DEVELOPER' | 'UNREGISTERED' | 'OVERSEAS';
export type BillingDocumentType = 'TAX_INVOICE' | 'INTERNAL_STATEMENT';

/**
 * Who an organisation is on an invoice. camelCase exactly as the API sends it.
 * Read-only on the wire: organizationName, configured, issuedByOrganizationName,
 * updatedBy, updatedAt. Dates (lutValidFrom/To) are ISO `YYYY-MM-DD`.
 */
export interface BillingProfileDTO {
  organizationId: string;
  organizationName?: string | null;
  configured: boolean;
  legalName?: string | null;
  tradeName?: string | null;
  addressLine1?: string | null;
  addressLine2?: string | null;
  city?: string | null;
  state?: string | null;
  stateCode?: string | null;
  postalCode?: string | null;
  countryCode?: string | null;
  gstRegistration?: GstRegistration | null;
  gstin?: string | null;
  pan?: string | null;
  taxId?: string | null;
  billingEmails?: string | null;
  phone?: string | null;
  issuedByOrganizationId?: string | null;
  issuedByOrganizationName?: string | null;
  documentType?: BillingDocumentType | null;
  invoiceCurrency?: string | null;
  invoicePrefix?: string | null;
  lutArn?: string | null;
  lutValidFrom?: string | null;
  lutValidTo?: string | null;
  defaultSac?: string | null;
  bankName?: string | null;
  bankAccountName?: string | null;
  bankAccountNumber?: string | null;
  bankIfsc?: string | null;
  bankSwift?: string | null;
  einvoiceEnabled?: boolean | null;
  updatedBy?: string | null;
  updatedAt?: string | null;
}

export interface CodeName {
  code: string;
  name: string;
}

/** GET /billing/profiles/reference: the lists the form picks from. */
export interface BillingProfileReference {
  states: CodeName[];
  countries: CodeName[];
  currencies: string[];
  gstRegistrations: GstRegistration[];
  documentTypes: BillingDocumentType[];
}

export const gstRegistrationLabels: Record<GstRegistration, string> = {
  REGULAR: 'Regular',
  COMPOSITION: 'Composition',
  SEZ_UNIT: 'SEZ unit',
  SEZ_DEVELOPER: 'SEZ developer',
  UNREGISTERED: 'Unregistered',
  OVERSEAS: 'Overseas',
};

export const documentTypeLabels: Record<BillingDocumentType, string> = {
  TAX_INVOICE: 'Tax invoice',
  INTERNAL_STATEMENT: 'Internal statement',
};


// ==================== Credit control (alerts, statements, enforcement) ====================

/** OFF: never refuses. SHADOW: logs what it would refuse, never refuses. ON: refuses. */
export type EnforceMode = 'OFF' | 'SHADOW' | 'ON';
export type SuspensionState = 'NONE' | 'SHADOW_SUSPENDED' | 'SUSPENDED' | 'OPERATOR_LIFTED';
export type AlertKind = 'BALANCE_ALERT' | 'DAILY_STATEMENT';
export type AlertStatus = 'SENT' | 'FAILED' | 'LOGGED' | 'NO_RECIPIENT' | 'SUPERSEDED';

/** Spring's Page, as the credit-control lists send it (`number` is 0-based). */
export interface SpringPage<T> {
  content: T[];
  totalElements: number;
  totalPages: number;
  size: number;
  number: number;
}

/** One balance alert or daily statement that was sent (or tried). Times are server UTC. */
export interface BalanceAlertDTO {
  id: number;
  kind: AlertKind;
  tier?: string | null;
  thresholdAmount?: number | null;
  availableBalance?: number | null;
  lastTopupTxId?: number | null;
  lastTopupAmount?: number | null;
  /** IST day, `YYYY-MM-DD`, for a daily statement. */
  statementDate?: string | null;
  currency?: string | null;
  channel?: string | null;
  status: AlertStatus;
  recipients?: string | null;
  detail?: string | null;
  attempts?: number | null;
  createdAt: string;
  sentAt?: string | null;
}

export interface AlertTierStatus {
  tier: string;
  thresholdAmount?: number | null;
  /** The balance is at or below this tier since the last top-up. */
  crossed: boolean;
  status?: AlertStatus | null;
  sentAt?: string | null;
}

export interface AlertSettingsDTO {
  organizationId: string;
  billingType?: BillingType | string;
  currency?: string;
  alertTierPercents: number[];
  usingDefaultTiers: boolean;
  defaultTierPercents: number[];
  lowBalanceThreshold?: number | null;
  availableBalance?: number | null;
  lastTopUp?: { transactionId: number; amount: number; occurredAt: string } | null;
  recipients: string[];
  tiers: AlertTierStatus[];
}

/** PUT alert-settings: every field optional, omitted = unchanged. */
export interface AlertSettingsRequest {
  alertTierPercents?: number[];
  useDefaultTiers?: boolean;
  lowBalanceThreshold?: number;
}

export interface UsageByService {
  serviceType: string;
  amount: number;
  charges: number;
  billedMinutes?: number | null;
}

/** One IST day's opening -> closing roll-forward. */
export interface DailyStatementDTO {
  organizationId: string;
  date: string;
  currency: string;
  openingBalance: number;
  topUps: number;
  usage: number;
  usageByService: UsageByService[];
  otherCredits: number;
  otherDebits: number;
  closingBalance: number;
}

export interface EnforcementDTO {
  organizationId: string;
  billingType?: BillingType | string;
  currency?: string;
  /** The account's own mode; null = following the platform default. */
  enforceMode: EnforceMode | null;
  effectiveMode: EnforceMode;
  globalEnforce?: EnforceMode | string | null;
  /** The most the platform allows any account to be set to. */
  maxMode?: EnforceMode | string | null;
  graceAmount?: number | null;
  /** The balance below which cpass-originated usage is refused (-grace). */
  floor?: number | null;
  autoSuspend: boolean;
  availableBalance?: number | null;
  suspensionState: SuspensionState;
  activeLockIds: string[];
  /** Whether the tata switches refuse this account's calls; absent from older servers. */
  switchGate?: SwitchGateDTO | null;
}

/**
 * The account as the tata switches' balance gate sees it. `listed` = on the gate list
 * (balance at or below -grace); `mode` ENFORCE refuses its calls, SHADOW only logs.
 */
export interface SwitchGateDTO {
  listed: boolean;
  mode: 'ENFORCE' | 'SHADOW' | null;
  /** Business-id patterns and DIDs the gate entry covers. */
  patterns: number;
  dids: number;
}

/** GET convert-to-prepaid/preview: what converting at `asOf` would find. */
export interface ConvertPreviewDTO {
  organizationId: string;
  billingType: BillingType | string;
  currency: string;
  balance: number;
  /** Ledger charges before asOf that no usage invoice covers yet. */
  uninvoicedCharges: number;
  uninvoicedAmount: number;
  /** Usage invoices still unpaid. */
  openUsageInvoices: number;
  canConvert: boolean;
  /** True when only uninvoiced usage blocks it, and that was invoiced outside cpass. */
  canConvertWithOutsideSettlement: boolean;
  balanceAfterSettlement: number;
}

/** GET shortfall: what a prepaid account below zero owes. */
export interface ShortfallDTO {
  balance: number;
  shortfall: number;
  openShortfallInvoiceId: number | null;
}

export interface EnforcementRequest {
  enforceMode?: EnforceMode;
  useGlobalDefault?: boolean;
  graceAmount?: number;
  autoSuspend?: boolean;
}

export interface SuspensionEventDTO {
  id: number;
  action: string;
  mode: string;
  applied: boolean;
  lockId?: string | null;
  availableBalance?: number | null;
  floorAmount?: number | null;
  reason?: string | null;
  createdAt: string;
}

// ==================== Reports ====================

export interface InvoicePreviewLine {
  businessId?: string | null;
  business?: string | null;
  serviceType?: string | null;
  carrier?: string | null;
  description: string;
  quantity: number;
  unit: 'min' | 'x' | string;
  unitPrice: number;
  billedMinutes?: number | null;
  charges: number;
  amount: number;
  firstOccurredAt?: string | null;
  lastOccurredAt?: string | null;
}

/**
 * "The invoice so far": what invoicing usage would produce now, nothing written. Times
 * (before, first/lastOccurredAt) are server UTC; billingPeriodStart/End are IST days.
 */
export interface InvoicePreviewDTO {
  organizationId: string;
  organizationName?: string | null;
  billingType?: string;
  /** False for a prepaid account: the top-up already paid; see `note`. */
  invoiceable: boolean;
  note?: string | null;
  currency: string;
  before: string;
  firstOccurredAt?: string | null;
  lastOccurredAt?: string | null;
  billingPeriodStart?: string | null;
  billingPeriodEnd?: string | null;
  charges: number;
  billedMinutes?: number | null;
  subtotal: number;
  taxRate: number;
  taxAmount: number;
  total: number;
  lines: InvoicePreviewLine[];
}

export interface UsageFigures {
  charges: number;
  billedMinutes?: number | null;
  amount: number;
}

export interface BusinessUsageDTO extends UsageFigures {
  businessId: string;
  businessName?: string | null;
  /** The organisation's own usage, not a business it pays for. */
  direct: boolean;
}

export interface KeyedUsage extends UsageFigures {
  key: string;
}

export interface DailyUsage extends UsageFigures {
  day: string;
}

export interface UsageLine extends UsageFigures {
  businessId: string;
  businessName?: string | null;
  serviceType: string;
  carrier?: string | null;
}

export interface UsageDashboardDTO {
  organizationId: string;
  organizationName?: string | null;
  from: string;
  to: string;
  businessId?: string | null;
  totals: UsageFigures;
  byBusiness: BusinessUsageDTO[];
  byServiceType: KeyedUsage[];
  byCarrier: KeyedUsage[];
  daily: DailyUsage[];
  lines: UsageLine[];
  /** Test calls (our team's numbers): not in the usage above, charged to Teeoff internally. */
  testCalls?: TestCallSummary[];
}

export interface TestCallSummary {
  businessId: string | null;
  businessName: string;
  month: string;
  calls: number;
  answered: number;
  billedMinutes: number;
  internalCost: number | null; // null unless platform staff: Teeoff's own cost of test calls
}

// ── Test call numbers ────────────────────────────────────────────────────────

export type TestNumberState = 'ACTIVE' | 'SCHEDULED' | 'ENDED' | 'DISABLED';

export interface TestNumberDTO {
  id: number;
  number: string;
  numberKey: string;
  label: string;
  note?: string | null;
  /** Server UTC LocalDateTime. */
  validFrom: string;
  validUntil?: string | null;
  active: boolean;
  state: TestNumberState;
  /** No scope rows: applies to every business. */
  allBusinesses: boolean;
  scope: { id: string; name: string }[];
  createdBy?: string | null;
  createdAt?: string | null;
  updatedAt?: string | null;
}

/** Create, or update with only the fields to change. organizationIds: [] = all businesses. */
export interface TestNumberRequest {
  number?: string;
  label?: string;
  note?: string;
  validFrom?: string;
  validUntil?: string;
  openEnded?: boolean;
  active?: boolean;
  organizationIds?: string[];
}

export interface TestCallNumberLine {
  numberKey: string;
  label: string;
  calls: number;
  answered: number;
  billedMinutes: number;
  internalCost: number;
}

export interface TestCallBusinessLine {
  businessId: string | null;
  businessName: string;
  providerId?: string | null;
  providerName?: string | null;
  calls: number;
  answered: number;
  billedMinutes: number;
  internalCost: number;
  byNumber: TestCallNumberLine[];
}

export interface TestCallLine {
  callId: string;
  occurredAt: string;
  direction: string | null;
  otherParty: string | null;
  numberKey: string;
  label: string;
  businessId: string | null;
  businessName: string;
  providerId?: string | null;
  providerName?: string | null;
  billedSeconds: number;
  answered: boolean;
  billedMinutes: number;
  internalCost: number;
  internalTransactionId?: number | null;
  origin?: string | null;
  stream?: string | null;
  carrier?: string | null;
}

export interface TestCallReportDTO {
  from: string;
  to: string;
  organizationId?: string | null;
  calls: number;
  answered: number;
  billedMinutes: number;
  internalCost: number;
  byBusiness: TestCallBusinessLine[];
  byNumber: TestCallNumberLine[];
  callList: TestCallLine[];
  truncated: boolean;
}

export type TestAdjustmentStatus = 'PENDING' | 'INVOICED' | 'ALREADY_ADJUSTED' | 'REFUNDED';

export interface TestAdjustmentCharge {
  transactionId: number;
  payerId: string;
  payerName: string;
  billingType?: string | null;
  currency: string;
  idempotencyKey: string;
  amount: number;
  billedMinutes?: number | null;
  invoiceId?: number | null;
  status: TestAdjustmentStatus;
}

export interface TestAdjustmentPayerLine {
  payerId: string;
  payerName: string;
  billingType?: string | null;
  currency: string;
  businessId: string | null;
  businessName: string;
  pendingCharges: number;
  pendingAmount: number;
  invoicedCharges: number;
  invoicedAmount: number;
  doneCharges: number;
  doneAmount: number;
}

export interface TestAdjustmentBusinessLine {
  businessId: string | null;
  businessName: string;
  providerId?: string | null;
  providerName?: string | null;
  calls: number;
  pendingCharges: number;
  pendingAmount: number;
  invoicedCharges: number;
  invoicedAmount: number;
  payers: TestAdjustmentPayerLine[];
}

export interface TestAdjustmentCall {
  callId: string;
  stream: string;
  occurredAt: string;
  direction: string;
  otherParty: string;
  testNumberId: number;
  testNumberLabel: string;
  businessId: string | null;
  businessName: string;
  providerId?: string | null;
  providerName?: string | null;
  billedSeconds: number;
  internalRecorded: boolean;
  charges: TestAdjustmentCharge[];
}

export interface TestAdjustmentPreviewDTO {
  from: string;
  testNumbers: number;
  callCount: number;
  pendingCharges: number;
  pendingAmount: number;
  invoicedCharges: number;
  invoicedAmount: number;
  alreadyAdjusted: number;
  internalRowsToWrite: number;
  byBusiness: TestAdjustmentBusinessLine[];
  byPayer: TestAdjustmentPayerLine[];
  calls: TestAdjustmentCall[];
  invoiced: TestAdjustmentCharge[];
  previewHash: string;
}

export interface TestAdjustmentResultDTO {
  from: string;
  reversed: number;
  reversedAmount: number;
  byPayer: TestAdjustmentPayerLine[];
  internalRowsWritten: number;
  failures: { transactionId: number | null; reason: string }[];
  invoicedUntouched: TestAdjustmentCharge[];
}

export interface DeferredRevenueAccount {
  organizationId: string;
  organizationName?: string | null;
  billingType?: string;
  currency: string;
  opening: number;
  topUps: number;
  consumption: number;
  adjustments: number;
  expired: number;
  closing: number;
}

export interface DeferredRevenueTotal {
  currency: string;
  accounts: number;
  opening: number;
  topUps: number;
  consumption: number;
  adjustments: number;
  expired: number;
  closing: number;
}

export interface DeferredRevenueDTO {
  month: string;
  fromUtc: string;
  toUtc: string;
  organizationId?: string | null;
  accounts: DeferredRevenueAccount[];
  totals: DeferredRevenueTotal[];
}

// ==================== Credit grants (expiring credit) ====================

export type CreditSource = 'PURCHASE' | 'PROMO' | 'ADJUSTMENT';
export type GrantStatus = 'ACTIVE' | 'EXHAUSTED' | 'EXPIRED' | 'VOID';

export const creditSourceLabels: Record<CreditSource, string> = {
  PURCHASE: 'Purchase',
  PROMO: 'Promotional',
  ADJUSTMENT: 'Adjustment',
};

export const grantStatusLabels: Record<GrantStatus, string> = {
  ACTIVE: 'Active',
  EXHAUSTED: 'Used up',
  EXPIRED: 'Expired',
  VOID: 'Void',
};

/** One grant of credit. Times (effectiveAt, expiresAt, closedAt, createdAt) are server UTC. */
export interface CreditGrantDTO {
  id: number;
  organizationId: string;
  source: CreditSource | string;
  status: GrantStatus;
  originalAmount: number;
  remainingAmount: number;
  priority: number;
  effectiveAt?: string | null;
  expiresAt?: string | null;
  creditTransactionId?: number | null;
  createdBy?: string | null;
  description?: string | null;
  closedAt?: string | null;
  createdAt: string;
  currency: string;
}

/** GET /credits/grants/expiring: what lapses in the next `days` days. */
export interface ExpiringGrantsDTO {
  organizationId: string;
  currency: string;
  days: number;
  from: string;
  until: string;
  creditBalance: number;
  grantedRemaining: number;
  /** Positive = owed: the balance is below what the grants still hold. */
  overdraft: number;
  expiringAmount: number;
  expiringCount: number;
  expiring: CreditGrantDTO[];
}
